/**
 * Product changes that can move pre → post growth, completion rates,
 * or who is in the measured sample. Dates are ship days from git
 * on the platform repo — not the date a given org turned a setting on.
 */

export type ChangeLogCategory =
    | "sessions"
    | "assessment"
    | "access"
    | "reminders"
    | "organization"
    | "measurement";

export interface EffectivenessChange {
    id: string;
    /** ISO date (YYYY-MM-DD) when the change shipped. */
    date: string;
    title: string;
    category: ChangeLogCategory;
    summary: string;
    measurementNote: string;
}

export const CHANGE_LOG_CATEGORIES: { id: ChangeLogCategory; label: string }[] = [
    { id: "assessment", label: "Assessment" },
    { id: "sessions", label: "Sessions" },
    { id: "access", label: "Login & onboarding" },
    { id: "reminders", label: "Reminders" },
    { id: "organization", label: "Organization admin" },
    { id: "measurement", label: "How growth is reported" },
];

export const EFFECTIVENESS_CHANGE_LOG: EffectivenessChange[] = [
    {
        id: "lesson-plans-introduced",
        date: "2025-11-19",
        title: "12-session lesson plans",
        category: "sessions",
        summary:
            "Curriculum moved from ad-hoc coach content to a 12-session, per-role lesson plan with a visible script.",
        measurementNote:
            "Pre → post comparison only becomes meaningful for people who actually work the sessions. Early cohorts may have incomplete or prototype session content.",
    },
    {
        id: "email-reminders",
        date: "2025-11-23",
        title: "Email reminders",
        category: "reminders",
        summary:
            "The first reminder loop: coaches and participants get email nudges to start or return to the program.",
        measurementNote:
            "Completion of the pre-assessment, sessions, and post-assessment can rise after reminders exist, which changes who is in the measured sample.",
    },
    {
        id: "notifications-mood-checkin",
        date: "2025-12-03",
        title: "In-app notifications and daily mood check-in",
        category: "reminders",
        summary:
            "Notifications moved into the product, and a short daily mood check-in was added alongside the program.",
        measurementNote:
            "Mood check-ins do not change domain scores, but they can change how often people return and finish the 12 sessions.",
    },
    {
        id: "new-assessment-questions",
        date: "2026-01-18",
        title: "New assessment questions",
        category: "assessment",
        summary:
            "The Confidence Assessment items and supporting emails were replaced with a new question set.",
        measurementNote:
            "This is an instrument change. Domain means from before this month are not the same scale as later ones. A person's pre and post must use the same question set.",
    },
    {
        id: "session-selector-single-active",
        date: "2026-01-19",
        title: "Session selector lands on the next unstarted session",
        category: "sessions",
        summary:
            "The lesson plan keeps one active session and slide. Opening a plan jumps to the next unstarted session instead of leaving people to browse.",
        measurementNote:
            "Session order and how much of the curriculum is actually delivered can change. Completion is less likely to skip around.",
    },
    {
        id: "ngu-points",
        date: "2026-02-05",
        title: "NGU points for assessments and sessions",
        category: "reminders",
        summary:
            "Points were added for finishing the pre-assessment, slides, sessions, and the post-assessment.",
        measurementNote:
            "Incentives can lift completion and may change how carefully people answer. Treat cohorts before and after points as different engagement conditions.",
    },
    {
        id: "daily-digest",
        date: "2026-03-10",
        title: "Daily email digest",
        category: "reminders",
        summary:
            "Most program emails were rolled into one nightly digest. The first notice of the day still goes out immediately.",
        measurementNote:
            "Coach review and athlete return can happen later in the day. Session cadence and time from pre-assessment to first session can stretch.",
    },
    {
        id: "post-assessment-guards",
        date: "2026-03-14",
        title: "Post-assessment timing and org guard",
        category: "measurement",
        summary:
            "The post-assessment was gated to the intended program point and could be limited by organization. Agents no longer run after a post is submitted.",
        measurementNote:
            "Who appears in pre → post deltas, and when the post score is collected, changed. Earlier posts may have been taken too early or after extra generated content.",
    },
    {
        id: "voice-assessment",
        date: "2026-04-08",
        title: "Voice assessment",
        category: "assessment",
        summary:
            "Participants can take the same Likert assessment by speaking with a voice facilitator instead of tapping answers. The spoken path was refined through July.",
        measurementNote:
            "Same items, different mode. Voice vs text can shift scores even when the construct is unchanged. Flag any pre/post pair that mixed the two modes.",
    },
    {
        id: "growth-reports",
        date: "2026-05-12",
        title: "Week 4, 8, and 12 growth reports",
        category: "measurement",
        summary:
            "Growth reports were queued and shown at program weeks 4, 8, and 12, separate from the closing post-assessment.",
        measurementNote:
            "Mid-program checkpoints are not the same as pre → post. Do not pool week-4/8 deltas with the closing comparison.",
    },
    {
        id: "conversational-facilitator",
        date: "2026-05-02",
        title: "Conversational lesson facilitator",
        category: "sessions",
        summary:
            "A voice facilitator can lead a session as a conversation around the slides, not only as a human reading the script.",
        measurementNote:
            "Facilitation quality and time-on-task can differ from a human-led session. Later script-reader mode is a different treatment again.",
    },
    {
        id: "invites-and-waivers",
        date: "2026-06-18",
        title: "Assignment invitations and role waivers",
        category: "access",
        summary:
            "Coaches, guardians, and payers invite people onto an assignment. Role waivers must be accepted before content, invites, and most program emails.",
        measurementNote:
            "The waiver gate changes who starts the pre-assessment and who receives reminders. Sample composition can shift when the gate moves.",
    },
    {
        id: "session-pacing",
        date: "2026-06-23",
        title: "Lesson start dates and session pacing",
        category: "sessions",
        summary:
            "Programs gained a start date and expected weekly pace, shown in the directory and progress views.",
        measurementNote:
            "On-track vs behind labeling can change reminder pressure and when sessions (and the post-assessment) actually happen.",
    },
    {
        id: "onboarding-intake-sso",
        date: "2026-07-05",
        title: "Intake surveys, SSO, and streamlined onboarding",
        category: "access",
        summary:
            "Role-specific intake surveys, prepaid SSO auto-provision, and a shorter purchase / invitation path went live in the same month.",
        measurementNote:
            "Who reaches the pre-assessment, and how much friction they hit first, changed by lane (self-pay, SSO, invite). Later cohorts are not the same entry sample.",
    },
    {
        id: "ai-coaching-mode",
        date: "2026-07-12",
        title: "AI coaching mode",
        category: "organization",
        summary:
            "An assignment can run without a human coach. Generated feedback and lesson plans follow a different publish path.",
        measurementNote:
            "Human-coached and AI-coached assignments are different treatments. Split them when comparing pre → post growth.",
    },
    {
        id: "session-ui-redesign",
        date: "2026-07-26",
        title: "Lesson plan redesign and session selector UI",
        category: "sessions",
        summary:
            "The session screen and how people pick a session were redesigned. The 30/60-minute pace selector was removed the next day.",
        measurementNote:
            "Navigation friction and planned session length both changed. How completely each of the 12 sessions is delivered can move with the UI.",
    },
    {
        id: "script-reader",
        date: "2026-07-28",
        title: "Script reader (scripted lesson voice)",
        category: "sessions",
        summary:
            "A second voice mode reads the slide script verbatim, records the spoken answer, and waits for next / repeat / reframe. It does not advise.",
        measurementNote:
            "Script-reader sessions are a different facilitation than conversational voice or a human coach. Do not pool those session types when reading growth.",
    },
    {
        id: "post-assessment-invite",
        date: "2026-07-21",
        title: "Post-assessment invite and final reflection",
        category: "assessment",
        summary:
            "Finishing the last entitled session now invites the participant to the post-assessment and an optional final reflection.",
        measurementNote:
            "Post-assessment completion (the share of people in the pre → post picture) is sensitive to this invite. Earlier cohorts had to find the post on their own.",
    },
    {
        id: "login-and-menu",
        date: "2026-08-08",
        title: "Sign-in in the main menu, Apple login, and in-app purchase",
        category: "access",
        summary:
            "Sign in and sign up moved into the main menu. Apple login and iOS in-app purchase were added beside Google and magic-link email.",
        measurementNote:
            "Easier return and a new mobile purchase path change who starts and who comes back, especially on iOS.",
    },
    {
        id: "simplified-onboarding-self-coach",
        date: "2026-08-24",
        title: "Simplified intake and AI self-coaching",
        category: "access",
        summary:
            "Intake fields were cut back and a self-coaching path was opened so a participant can start without waiting on a human coach.",
        measurementNote:
            "Later cohorts mix self-coached and human-coached people. Time from signup to first session is shorter on the self-coach path.",
    },
    {
        id: "multiple-assignments",
        date: "2026-08-26",
        title: "Multiple organization assignments per athlete",
        category: "organization",
        summary:
            "One person can hold more than one program assignment across organizations.",
        measurementNote:
            "Assignment-level growth is not the same as unique people. De-duplicate before reporting org or platform effectiveness.",
    },
    {
        id: "org-wide-regen",
        date: "2026-08-12",
        title: "Organization-wide lesson plan regeneration",
        category: "organization",
        summary:
            "Admins can regenerate lesson plans for a whole organization instead of one assignment at a time.",
        measurementNote:
            "Curriculum quality and the timing of session 1 can jump mid-cohort if an org regenerates while people are already in progress.",
    },
    {
        id: "session-notes",
        date: "2026-09-02",
        title: "Session note taker",
        category: "sessions",
        summary:
            "Coaches and guardians can take private notes on a session. Athletes do not see those notes.",
        measurementNote:
            "Notes do not change Likert items, but they can change how later sessions are facilitated and what observers write at weeks 4, 8, and 12.",
    },
    {
        id: "auto-publish",
        date: "2026-09-02",
        title: "Organization auto-publish stages",
        category: "organization",
        summary:
            "An organization can auto-publish generated feedback, curriculum, and lesson plans instead of waiting on a coach to click Publish.",
        measurementNote:
            "Time from pre-assessment to first session shrinks when this is on. Only compare auto-publish orgs to other auto-publish orgs.",
    },
    {
        id: "session-scheduler",
        date: "2026-09-28",
        title: "Session scheduler",
        category: "sessions",
        summary:
            "Pairs can pick session times in the product. The scheduler ties into reminders and the program calendar.",
        measurementNote:
            "Scheduled cadence can raise session completion and change when the post-assessment is reached.",
    },
    {
        id: "subscription-checkout",
        date: "2026-09-25",
        title: "Subscription-based checkout",
        category: "access",
        summary:
            "Self-pay moved onto membership plans that entitle a set number of sessions instead of a one-time product only.",
        measurementNote:
            "Trial or partial entitlements are a different dosage than a full 12-session program. Do not pool their post scores.",
    },
    {
        id: "reminders-before-waiver",
        date: "2026-10-01",
        title: "Assessment reminders before the waiver",
        category: "reminders",
        summary:
            "Reminder emails can send people into the assessment even if they have not signed the role waiver yet.",
        measurementNote:
            "The pre-assessment sample can include people who previously never started. Completion of pre may rise without a matching rise in session or post completion.",
    },
    {
        id: "login-and-waiver-gate",
        date: "2026-10-15",
        title: "Login screens and a simpler waiver gate",
        category: "access",
        summary:
            "Sign-in screens were rewritten and the waiver gate was simplified so fewer people get stuck before the program.",
        measurementNote:
            "Lower friction can raise pre-assessment starts and return visits for the post. Later completion rates are not a clean read on program quality alone.",
    },
    {
        id: "question-admin",
        date: "2026-10-06",
        title: "Assessment question administration",
        category: "organization",
        summary:
            "Staff can manage assessment questions in admin (wording, order, and related settings) without a code deploy.",
        measurementNote:
            "Any item or order change is an instrument change. Treat the next assessments as a new baseline until the same form is used on both pre and post.",
    },
];
